import React, { useState, useEffect, useRef } from "react";
import { Link } from 'react-router-dom';
import Geohublogo from './styles/Geohublogo.svg';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-regular-svg-icons';
import { useNavigate, useLocation } from "react-router-dom";
import API_CONFIG from './config';
import Disclaimer from './Disclaimer';

// Utility function to decode JWT payload safely
const decodeJWTPayload = (token) => {
  try {
    const payloadBase64 = token.split('.')[1];
    if (!payloadBase64) throw new Error('Invalid token format');
    return JSON.parse(atob(payloadBase64));
  } catch (error) {
    console.error('Failed to decode token:', error);
    return null;
  }
};

// Utility functions
const refreshAccessToken = async (setIsLoginModalOpen) => {
  try {
    const refreshToken = localStorage.getItem('refresh_token');
    if (!refreshToken) {
      throw new Error('No refresh token available');
    }

    // console.log('Refreshing access token...');

    const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.AUTH.REFRESH}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refresh_token: refreshToken }),
    });

    const data = await response.json();

    if (response.ok) {
      // console.log('Token refresh successful, updating localStorage');

      // Remove old tokens first
      localStorage.removeItem('access_token');
      localStorage.removeItem('authToken');

      // Store new tokens from refresh response
      localStorage.setItem('access_token', data.access_token);
      localStorage.setItem('authToken', data.access_token); // Also update authToken for compatibility

      // Update refresh token if provided
      if (data.refresh_token) {
        localStorage.removeItem('refresh_token');
        localStorage.setItem('refresh_token', data.refresh_token);
      }

      // Extract and store user info if available
      if (data.user) {
        localStorage.setItem('userId', data.user.id);
        localStorage.setItem('userEmail', data.user.email);
        localStorage.setItem('userName', data.user.name);
      } else {
        // If user info not directly available, try to extract user_id from token
        try {
          const payload = decodeJWTPayload(data.access_token);
          if (payload && payload.user_id) {
            localStorage.setItem('userId', payload.user_id);
          }
          if (payload && payload.email) {
            localStorage.setItem('userEmail', payload.email);
          }
        } catch (error) {
          console.error('Error extracting user ID from token:', error);
        }
      }

      return data.access_token;
    } else {
      throw new Error(data.message || 'Token refresh failed');
    }
  } catch (error) {
    console.error('Token refresh failed:', error);
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    localStorage.removeItem('authToken');
    localStorage.removeItem('userId');
    localStorage.removeItem('userEmail');
    localStorage.removeItem('userName');

    if (setIsLoginModalOpen) {
      setIsLoginModalOpen(true);
    }
    return null;
  }
};

const createAuthenticatedRequest = async (url, options = {}, setIsLoginModalOpen) => {
  let accessToken = localStorage.getItem('access_token');

  try {
    // First attempt with current access token
    // console.log('Making authenticated request with current token');
    const response = await fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        'Authorization': `Bearer ${accessToken}`,
        'Content-Type': options.headers?.['Content-Type'] || 'application/json',
      },
    });

    // If unauthorized, try to refresh the token and retry the request
    if (response.status === 401) {
      // console.log('Request unauthorized (401), attempting to refresh token');
      const newToken = await refreshAccessToken(setIsLoginModalOpen);

      if (newToken) {
        // console.log('Token refreshed successfully, retrying request with new token');
        // Get the fresh token from localStorage after refresh
        accessToken = localStorage.getItem('access_token');

        // Retry the request with the new token
        return fetch(url, {
          ...options,
          headers: {
            ...options.headers,
            'Authorization': `Bearer ${accessToken}`,
            'Content-Type': options.headers?.['Content-Type'] || 'application/json',
          },
        });
      } else {
        // If token refresh failed, throw an error
        console.error('Authentication failed and token refresh was unsuccessful');
        throw new Error('Authentication failed and token refresh was unsuccessful');
      }
    }

    return response;
  } catch (error) {
    console.error('Request failed:', error);
    throw error;
  }
};

const isTokenExpired = () => {
  const token = localStorage.getItem('access_token');
  if (!token) return true;

  try {
    const payload = decodeJWTPayload(token);
    if (!payload || !payload.exp) return true;

    const currentTime = Math.floor(Date.now() / 1000);
    return payload.exp < currentTime;
  } catch (error) {
    console.error('Error checking token expiration:', error);
    return true;
  }
};

// Global token validation function that can be used throughout the app
const validateToken = async () => {
  const token = localStorage.getItem('authToken') || localStorage.getItem('access_token');

  // If no token exists, redirect to login
  if (!token) {
    console.warn('No auth token found, redirecting to login');
    window.location.href = '/'; // Redirect to root which will show login
    return false;
  }

  // Check if token is expired
  try {
    const payload = decodeJWTPayload(token);
    if (!payload || !payload.exp) {
      console.warn('Invalid token format, redirecting to login');
      clearAuthTokens();
      window.location.href = '/';
      return false;
    }

    const currentTime = Math.floor(Date.now() / 1000);
    if (payload.exp < currentTime) {
      console.warn('Token expired, attempting refresh...');
      
      try {
        // Attempt to refresh the token
        const newToken = await refreshAccessToken(null);
        if (newToken) {
          console.log('Token refreshed successfully in validateToken');
          return true;
        } else {
          console.warn('Token refresh failed, redirecting to login');
          clearAuthTokens();
          window.location.href = '/';
          return false;
        }
      } catch (error) {
        console.error('Token refresh error in validateToken:', error);
        clearAuthTokens();
        window.location.href = '/';
        return false;
      }
    }

    return true;
  } catch (error) {
    console.error('Error validating token:', error);
    clearAuthTokens();
    window.location.href = '/';
    return false;
  }
};

// Helper function to clear all auth tokens
const clearAuthTokens = () => {
  localStorage.removeItem('access_token');
  localStorage.removeItem('refresh_token');
  localStorage.removeItem('authToken');
  localStorage.removeItem('userId');
  localStorage.removeItem('userEmail');
  localStorage.removeItem('userName');
  // Reset free chat count when clearing auth
  localStorage.removeItem('geoHubFreeChatCount');
};

const Login = () => {
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const [userType, setUserType] = useState("organization");
  const [isDisclaimerOpen, setIsDisclaimerOpen] = useState(false);
  const [isLoginView, setIsLoginView] = useState(true);
  const [signupStep, setSignupStep] = useState(1);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [otp, setOtp] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [otpError, setOtpError] = useState(null);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState("");
  const [forgotPasswordError, setForgotPasswordError] = useState(null);
  const [passwordResetSuccess, setPasswordResetSuccess] = useState(false);
  const [resendOtpError, setResendOtpError] = useState(null);
  const [resendOtpSuccess, setResendOtpSuccess] = useState(null);
  const [loginError, setLoginError] = useState(null);
  const [emptyFieldsError, setEmptyFieldsError] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [resetPasswordStep, setResetPasswordStep] = useState(1);
  const [passwordMatchError, setPasswordMatchError] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();
  const [loading, setLoading] = useState(false);
  const [isSignupMode, setIsSignupMode] = useState(false);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [showForgotPasswordModal, setShowForgotPasswordModal] = useState(false);
  const [showResetOTPModal, setShowResetOTPModal] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [signupEmail, setSignupEmail] = useState('');
  const [signupPassword, setSignupPassword] = useState('');
  const [signupConfirmPassword, setSignupConfirmPassword] = useState('');
  const [signupName, setSignupName] = useState('');
  const [signupError, setSignupError] = useState('');
  const [is2FAEnabled, setIs2FAEnabled] = useState(false);
  const [showOTPModal, setShowOTPModal] = useState(false);
  const [userId, setUserId] = useState(null);
  const [verificationCount, setVerificationCount] = useState(0);
  const [showDisclaimerPopup, setShowDisclaimerPopup] = useState(false);
  const [agreedToDisclaimer, setAgreedToDisclaimer] = useState(false);
  const [resetPasswordToken, setResetPasswordToken] = useState('');
  const [resetOtp, setResetOtp] = useState('');
  const [resetOtpError, setResetOtpError] = useState('');
  const [resetEmail, setResetEmail] = useState('');
  const instanceId = useRef(`login-${Math.random().toString(36).substring(2, 9)}`);
  const [showDisclaimerMessage, setShowDisclaimerMessage] = useState(false);

  // Modify the first useEffect to check for disclaimer acknowledgment
  useEffect(() => {
    console.group(`Login Modal Visibility Check [${instanceId.current}]`);
    
    // IMPORTANT: Default to NOT showing the login modal - directly set to false no matter what
    setIsLoginModalOpen(false);
    // console.log('Initially setting login modal to hidden');
    
    // Get the chat count from localStorage - make sure to parse it as an integer
    const chatCountRaw = localStorage.getItem('geoHubFreeChatCount');
    // console.log('Raw chat count from localStorage:', chatCountRaw);
    
    const chatCount = parseInt(chatCountRaw || '0', 10);
    // console.log('Parsed chat count:', chatCount);
    
    const MAX_FREE_CHATS = 5; // Maximum number of free chats
    
    // Check if we're coming from the chat limit reached scenario
    const fromChatLimit = sessionStorage.getItem('fromChatLimit') === 'true';
    // console.log('fromChatLimit flag in sessionStorage:', fromChatLimit);
    
    // Check if token exists
    const token = localStorage.getItem('authToken') || localStorage.getItem('access_token');
    const isAuthenticated = !!token;
    // console.log('User is authenticated:', isAuthenticated);
    // console.log('Current path:', location.pathname);
    
    // Check if the user has a valid unexpired disclaimer acknowledgment
    const hasAcknowledgedDisclaimer = hasValidDisclaimer();
    // console.log('User has valid disclaimer acknowledgment:', hasAcknowledgedDisclaimer);
    
    // If the user is authenticated but hasn't seen the disclaimer yet, show it
    if (isAuthenticated && !hasAcknowledgedDisclaimer) {
      // console.log('User is authenticated but has not seen disclaimer or disclaimer has expired, showing disclaimer');
      setTimeout(() => {
        setIsDisclaimerOpen(true);
      }, 100);
    }
    
    // Determine if we should show the login modal
    let shouldShowLogin = false;
    let reason = 'None - keeping modal hidden';
    
    if (location.pathname === '/Login') {
      // Case 1: Explicitly at the Login path
      shouldShowLogin = true;
      reason = 'User is explicitly at /Login path';
      // console.log('✅ ' + reason);
    } else if (fromChatLimit) {
      // Case 2: Redirected from chat limit popup
      shouldShowLogin = true;
      reason = 'User was redirected from chat limit popup';
      // console.log('✅ ' + reason);
      // Clear the flag
      sessionStorage.removeItem('fromChatLimit');
      // console.log('Cleared fromChatLimit flag from sessionStorage');
    } else if (!isAuthenticated && chatCount >= MAX_FREE_CHATS) {
      // Case 3: Not authenticated and has exceeded chat limit
      shouldShowLogin = true;
      reason = `Unauthenticated user has reached/exceeded chat limit (${chatCount}/${MAX_FREE_CHATS})`;
      // console.log('✅ ' + reason);
    } else {
      // console.log('❌ No conditions met to show login modal:');
      // console.log(`   - Path is not /Login (${location.pathname})`);
      // console.log(`   - Not redirected from chat limit (${fromChatLimit})`);
      // console.log(`   - Either authenticated (${isAuthenticated}) or below chat limit (${chatCount} < ${MAX_FREE_CHATS})`);
    }
    
    // console.log(`Final decision: ${shouldShowLogin ? 'SHOW' : 'HIDE'} login modal (Reason: ${reason})`);
    
    // Use timeout to ensure this executes after any other state updates
    setTimeout(() => {
      setIsLoginModalOpen(shouldShowLogin);
    }, 0);
    
    console.groupEnd();
  }, [location.pathname]);

  // Add a side effect to track any changes to the login modal state
  useEffect(() => {
    // console.log(`Login modal visibility is now: ${isLoginModalOpen ? 'VISIBLE' : 'HIDDEN'} [${instanceId.current}]`);
    
    // Check if there are other Login modals in the DOM
    const loginBackdrops = document.querySelectorAll('.login-backdrop');
    if (loginBackdrops.length > 1) {
      console.error(`WARNING: Multiple login backdrops detected (${loginBackdrops.length})!`);
      
      // Force hide all but the current one
      if (!isLoginModalOpen) {
        // This is safer than manipulating the DOM directly
        // console.log('This instance is supposed to be hidden, leaving it that way');
      }
    }
  }, [isLoginModalOpen]);

  // Add cleanup to component unmounting
  useEffect(() => {
    // console.log(`Login component mounted [${instanceId.current}]`);
    return () => {
      // console.log(`Login component unmounted [${instanceId.current}]`);
    };
  }, []);

  // Add a cleanup function that runs on component mount to handle chat limit scenarios
  useEffect(() => {
    console.group('Login Component - Chat Limit Handling');
    
    // Check if we're coming from the chat limit reached scenario
    const fromChatLimit = sessionStorage.getItem('fromChatLimit') === 'true';
    // console.log('fromChatLimit flag in sessionStorage:', fromChatLimit);
    
    if (fromChatLimit) {
      // console.log('User was redirected from chat limit popup, cleaning up Mermaid cache');
      
      // Clear Mermaid-specific cache
      try {
        // Clear any Mermaid cache in the window object
        if (window.mermaid && typeof window.mermaid.clear === 'function') {
          window.mermaid.clear();
          // console.log('Successfully cleared Mermaid cache');
          
          // Additional cleanup for Mermaid elements in DOM
          document.querySelectorAll('.mermaid, .mermaid-diagram, .mermaid-diagram-container').forEach(el => {
            if (el && el.parentNode) {
              try {
                el.parentNode.removeChild(el);
                // console.log('Removed Mermaid element from DOM');
              } catch (e) {
                console.warn('Error removing Mermaid element:', e);
              }
            }
          });
        }
        
        // Force cleanup of any error messages
        document.querySelectorAll('.mermaid-error, [class*="syntax-error"]').forEach(el => {
          if (el && el.parentNode) {
            try {
              el.parentNode.removeChild(el);
              // console.log('Removed error message element from DOM');
            } catch (e) {
              console.warn('Error removing error element:', e);
            }
          }
        });
        
        // Remove Mermaid-related localStorage items - include more keys
        const mermaidKeys = [
          'mermaid-diagram-cache',
          'mermaid-flowchart-config', 
          'mermaid-sequence-config',
          'mermaid-gantt-config',
          'mermaid-er-config',
          'mermaid-class-config',
          'syntax-error-cache',
          'diagram-render-status'
        ];
        
        mermaidKeys.forEach(key => {
          try {
            localStorage.removeItem(key);
          } catch (e) {
            console.warn(`Failed to remove ${key} from localStorage`, e);
          }
        });
        
        // Flush console logs if available
        if (console.clear) {
          console.clear();
          // console.log('Console cleared to remove any error logs');
        }
      } catch (error) {
        console.warn('Error clearing Mermaid cache:', error);
      }
      
      // Clear the flag after processing
      sessionStorage.removeItem('fromChatLimit');
      // console.log('Cleared fromChatLimit flag from sessionStorage');
    }
    
    console.groupEnd();
  }, []);
  
  useEffect(() => {
    console.group('Login Component - Modal Visibility Control');
    
    // Get chat count to determine if we should show login
    const chatCountRaw = localStorage.getItem('geoHubFreeChatCount');
    const chatCount = parseInt(chatCountRaw || '0', 10);
    // console.log('Parsed chat count:', chatCount);
    
    const MAX_FREE_CHATS = 5; // Maximum number of free chats
    
    // Check if we're coming from the chat limit reached scenario
    const fromChatLimit = sessionStorage.getItem('fromChatLimit') === 'true';
    // console.log('fromChatLimit flag in sessionStorage:', fromChatLimit);
    
    // Check if token exists
    const token = localStorage.getItem('authToken') || localStorage.getItem('access_token');
    const isAuthenticated = !!token;
    // console.log('User is authenticated:', isAuthenticated);
    // console.log('Current path:', location.pathname);
    
    // Check if the user has a valid unexpired disclaimer acknowledgment
    const hasAcknowledgedDisclaimer = hasValidDisclaimer();
    // console.log('User has valid disclaimer acknowledgment:', hasAcknowledgedDisclaimer);
    
    // Check if user is coming directly to login page and hasn't seen disclaimer
    if (location.pathname.toLowerCase() === '/login' && !hasAcknowledgedDisclaimer) {
      // console.log('User is at login path but has not seen disclaimer, showing message popup');
      setShowDisclaimerMessage(true);
      return; // Exit early, don't proceed with normal flow
    }
    
    // If the user is authenticated but hasn't seen the disclaimer yet, show it
    if (isAuthenticated && !hasAcknowledgedDisclaimer) {
      // console.log('User is authenticated but has not seen disclaimer or disclaimer has expired, showing disclaimer');
      setTimeout(() => {
        setIsDisclaimerOpen(true);
      }, 100);
    }
    
    // Determine if we should show the login modal
    let shouldShowLogin = false;
    let reason = 'None - keeping modal hidden';
    
    if (location.pathname.toLowerCase() === '/login') {
      // Case 1: Explicitly at the Login path
      shouldShowLogin = true;
      reason = 'User is explicitly at /Login path';
      // console.log('✅ ' + reason);
    } else if (fromChatLimit) {
      // Case 2: Redirected from chat limit popup
      shouldShowLogin = true;
      reason = 'User was redirected from chat limit popup';
      // console.log('✅ ' + reason);
      // Clear the flag
      sessionStorage.removeItem('fromChatLimit');
      // console.log('Cleared fromChatLimit flag from sessionStorage');
    } else if (!isAuthenticated && chatCount >= MAX_FREE_CHATS) {
      // Case 3: Not authenticated and has exceeded chat limit
      shouldShowLogin = true;
      reason = `Unauthenticated user has reached/exceeded chat limit (${chatCount}/${MAX_FREE_CHATS})`;
      // console.log('✅ ' + reason);
    } else {
      // console.log('❌ No conditions met to show login modal:');
      // console.log(`   - Path is not /Login (${location.pathname})`);
      // console.log(`   - Not redirected from chat limit (${fromChatLimit})`);
      // console.log(`   - Either authenticated (${isAuthenticated}) or below chat limit (${chatCount} < ${MAX_FREE_CHATS})`);
    }
    
    // console.log(`Final decision: ${shouldShowLogin ? 'SHOW' : 'HIDE'} login modal (Reason: ${reason})`);
    
    // Use timeout to ensure this executes after any other state updates
    setTimeout(() => {
      setIsLoginModalOpen(shouldShowLogin);
    }, 0);
    
    console.groupEnd();
  }, [location.pathname]);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const handleResendOtp = async (purpose = "verification") => {
    try {
      setLoading(true);
      setResendOtpError(null);
      setResendOtpSuccess(null);

      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.AUTH.RESEND_OTP}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, purpose }),
      });

      const data = await response.json();

      if (response.ok) {
        setResendOtpSuccess(data.message || 'OTP sent successfully');
      } else {
        throw new Error(data.message || 'Failed to resend OTP');
      }
    } catch (error) {
      setResendOtpError(error.message || 'Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPasswordClick = () => {
    setShowForgotPassword(true);
    setForgotPasswordEmail("");
    setForgotPasswordError(null);
    setPasswordResetSuccess(false);
  };

  const handleForgotPasswordSubmit = async () => {
    if (!forgotPasswordEmail) {
      setForgotPasswordError("Please enter your email address");
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.AUTH.FORGOT_PASSWORD}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email: forgotPasswordEmail }),
      });

      const data = await response.json();

      if (response.ok) {
        // Move to reset password step instead of showing success message
        setResetPasswordStep(2);
        setOtp(""); // Clear OTP field
        setNewPassword(""); // Clear new password field
        setConfirmPassword(""); // Clear confirm password field
        setForgotPasswordError(null);
      } else {
        setForgotPasswordError(data.message || 'Failed to send reset email');
      }
    } catch (error) {
      setForgotPasswordError('Failed to process request');
    } finally {
      setLoading(false);
    }
  };

  const handleLoginModalClose = () => {
    setIsLoginModalOpen(false);
    window.location.href = "https://www.encardio.com/";
  };

  // Helper function to navigate with reload
  const navigateWithReload = (path) => {
    // Store the path we want to go to
    sessionStorage.setItem('redirectAfterLogin', path);
    // Reload the page to ensure all components pick up authentication state
    window.location.href = path;
  };

  // Function to check if disclaimer has been seen and is still valid
  const hasValidDisclaimer = () => {
    const disclaimerDataRaw = localStorage.getItem('hasSeenDisclaimer');
    
    if (disclaimerDataRaw) {
      try {
        // Try to parse as JSON (new format with expiration)
        const disclaimerData = JSON.parse(disclaimerDataRaw);
        
        // Check if the disclaimer has expired
        if (disclaimerData && disclaimerData.expiresAt) {
          const now = new Date().getTime();
          if (now < disclaimerData.expiresAt) {
            // Disclaimer is still valid
            return true;
          } else {
            // Disclaimer has expired, remove it
            // console.log('Disclaimer has expired, will show again');
            localStorage.removeItem('hasSeenDisclaimer');
            return false;
          }
        } else if (disclaimerDataRaw === 'true') {
          // Handle legacy format (string 'true' without expiration)
          // For backward compatibility, consider it as valid but update to new format
          
          // Update to new format with expiration
          const expirationDate = new Date();
          expirationDate.setDate(expirationDate.getDate() + 7);
          
          const updatedDisclaimerData = {
            acknowledged: true,
            expiresAt: expirationDate.getTime()
          };
          
          localStorage.setItem('hasSeenDisclaimer', JSON.stringify(updatedDisclaimerData));
          // console.log('Updated legacy disclaimer format to include expiration');
          return true;
        }
      } catch (e) {
        // If parsing fails, it might be the old format (just 'true')
        if (disclaimerDataRaw === 'true') {
          // Update to new format with expiration
          const expirationDate = new Date();
          expirationDate.setDate(expirationDate.getDate() + 7);
          
          const updatedDisclaimerData = {
            acknowledged: true,
            expiresAt: expirationDate.getTime()
          };
          
          localStorage.setItem('hasSeenDisclaimer', JSON.stringify(updatedDisclaimerData));
          // console.log('Updated legacy disclaimer format to include expiration');
          return true;
        }
      }
    }
    
    return false;
  };

  const handleLogin = async () => {
    if (!email || !password) {
      setEmptyFieldsError("Please fill in both email and password.");
      return;
    }

    try {
      setLoading(true);
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.AUTH.TOKEN}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password }),
      });

      const data = await response.json();

      if (response.ok) {
        // Store tokens in both formats for compatibility
        localStorage.setItem('access_token', data.access_token);
        localStorage.setItem('refresh_token', data.refresh_token);
        localStorage.setItem('authToken', data.access_token); // Also store as authToken for compatibility

        // Save user information if available in the response
        if (data.user) {
          localStorage.setItem('userId', data.user.id);
          localStorage.setItem('userEmail', data.user.email);
          localStorage.setItem('userName', data.user.name);
        } else {
          // Extract and store userId from token if user data not directly provided
          try {
            const payload = decodeJWTPayload(data.access_token);
            if (payload && payload.user_id) {
              localStorage.setItem('userId', payload.user_id);
            }
            if (payload && payload.email) {
              localStorage.setItem('userEmail', payload.email);
            }
          } catch (error) {
            console.error('Error extracting user info from token:', error);
          }
        }

        // Reset the chat count after successful login
        localStorage.removeItem('geoHubFreeChatCount');
        // console.log('Chat count reset after login');

        // Check if user has seen the disclaimer before and if it's still valid
        if (!hasValidDisclaimer()) {
          // Show disclaimer if this is the first login or disclaimer has expired
          setIsLoginModalOpen(false);
          setIsDisclaimerOpen(true);
        } else {
          // Navigate directly to the chat page if already seen disclaimer
          setIsLoginModalOpen(false);
          navigateWithReload("/");
        }
      } else {
        throw new Error(data.message || 'Login failed');
      }
    } catch (error) {
      setLoginError(error.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const accessToken = queryParams.get("access_token");

    if (accessToken) {
        localStorage.setItem("authToken", accessToken);
        localStorage.setItem("access_token", accessToken);
        setIsLoginModalOpen(false);
        
        // Check if user has a valid disclaimer acknowledgment
        if (!hasValidDisclaimer()) {
            // Show disclaimer if this is the first login or disclaimer has expired
            setIsDisclaimerOpen(true);
        } else {
            // Directly navigate to the chat page
            navigateWithReload("/");
        }
    } else {
        const token = localStorage.getItem("authToken");
        if (!token) {
            setIsLoginModalOpen(true);
        }
    }
  }, [location]);

  const handleOAuthLogin = async (provider) => {
    try {
      setLoading(true);
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.AUTH.OAUTH}/${provider}`, {
        method: 'GET',
      });

      const data = await response.json();

      if (response.ok && data.url) {
        window.location.href = data.url;
      } else {
        throw new Error(`${provider} login failed`);
      }
    } catch (error) {
      setLoginError(`Error with ${provider} login`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const queryParams = new URLSearchParams(location.search);
    const provider = queryParams.get("provider");
    const code = queryParams.get("code");

    if (provider && code) {
      const handleOAuthCallback = async () => {
        if (code) {
          try {
            setLoading(true);
            const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.AUTH.OAUTH}/${provider}/callback?code=${code}`, {
              method: 'GET',
            });

            const data = await response.json();

            if (response.ok) {
              localStorage.setItem('access_token', data.access_token);
              localStorage.setItem('refresh_token', data.refresh_token);
              localStorage.setItem('authToken', data.access_token);

              // Save user information if available in the response
              if (data.user) {
                localStorage.setItem('userId', data.user.id);
                localStorage.setItem('userEmail', data.user.email);
                localStorage.setItem('userName', data.user.name);
              } else {
                // Extract and store userId from token
                try {
                  const payload = decodeJWTPayload(data.access_token);
                  if (payload && payload.user_id) {
                    localStorage.setItem('userId', payload.user_id);
                  }
                  if (payload && payload.email) {
                    localStorage.setItem('userEmail', payload.email);
                  }
                } catch (error) {
                  console.error('Error extracting user info from token:', error);
                }
              }

              // Check if user has a valid disclaimer acknowledgment
              if (!hasValidDisclaimer()) {
                // Show disclaimer if this is the first login or disclaimer has expired
                setIsLoginModalOpen(false);
                setIsDisclaimerOpen(true);
              } else {
                // Navigate directly to the chat page if already seen disclaimer
                setIsLoginModalOpen(false);
                navigateWithReload("/");
              }
            } else {
              throw new Error('OAuth callback failed');
            }
          } catch (error) {
            setLoginError('Failed to complete authentication');
          } finally {
            setLoading(false);
          }
        }
      };

      handleOAuthCallback();
    }
  }, [location]);

  const openDisclaimerPopup = () => {
    setIsDisclaimerOpen(true);
  };

  const handleUnderstandDisclaimer = () => {
    // Save that user has seen the disclaimer
    localStorage.setItem('hasSeenDisclaimer', 'true');
    setIsDisclaimerOpen(false);
    navigateWithReload("/");
  };

  const handleCloseDisclaimer = () => {
    // If user declines, still mark as seen but redirect to company website
    localStorage.setItem('hasSeenDisclaimer', 'true');
    setIsDisclaimerOpen(false);
    window.location.href = "https://www.encardio.com/";
  };

  const handleSignup = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.AUTH.REGISTER}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password, name }),
      });

      const data = await response.json();

      if (response.ok) {
        setSignupStep(2);
      } else {
        throw new Error(data.message || 'Registration failed');
      }
    } catch (error) {
      setLoginError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOtp = async () => {
    try {
      setLoading(true);
      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.AUTH.VERIFY_OTP}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, otp }),
      });

      const data = await response.json();

      if (response.ok) {
        // If OTP verification was successful and we got tokens in the response
        if (data.access_token && data.refresh_token) {
          // Store the tokens
          localStorage.setItem('access_token', data.access_token);
          localStorage.setItem('refresh_token', data.refresh_token);
          localStorage.setItem('authToken', data.access_token); // For compatibility

          // Save user information if available
          if (data.user) {
            localStorage.setItem('userId', data.user.id);
            localStorage.setItem('userEmail', data.user.email);
            localStorage.setItem('userName', data.user.name);
          } else {
            // Try to extract user ID from token
            try {
              const payload = decodeJWTPayload(data.access_token);
              if (payload && payload.user_id) {
                localStorage.setItem('userId', payload.user_id);
              }
              if (payload && payload.email) {
                localStorage.setItem('userEmail', payload.email);
              }
            } catch (error) {
              console.error('Error extracting user info from token:', error);
            }
          }

          // Check if user has a valid disclaimer acknowledgment
          if (!hasValidDisclaimer()) {
            // Show disclaimer if this is the first login or disclaimer has expired
            setIsLoginModalOpen(false);
            setIsDisclaimerOpen(true);
          } else {
            // Navigate directly to the chat page if already seen disclaimer
            setIsLoginModalOpen(false);
            setOtpError(null);
            navigateWithReload("/");
          }
        } else {
          // If verification succeeded but we didn't get tokens, try to log in automatically
          // console.log('OTP verification successful, attempting automatic login');
          try {
            // Only attempt auto-login if we have email and password
            if (email && password) {
              const loginResponse = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.AUTH.TOKEN}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email, password }),
              });

              const loginData = await loginResponse.json();

              if (loginResponse.ok) {
                // Store tokens from login response
                localStorage.setItem('access_token', loginData.access_token);
                localStorage.setItem('refresh_token', loginData.refresh_token);
                localStorage.setItem('authToken', loginData.access_token);

                // Extract and store user info
                if (loginData.user) {
                  localStorage.setItem('userId', loginData.user.id);
                  localStorage.setItem('userEmail', loginData.user.email);
                  localStorage.setItem('userName', loginData.user.name);
                } else {
                  try {
                    const payload = decodeJWTPayload(loginData.access_token);
                    if (payload && payload.user_id) {
                      localStorage.setItem('userId', payload.user_id);
                    }
                    if (payload && payload.email) {
                      localStorage.setItem('userEmail', payload.email);
                    }
                  } catch (error) {
                    console.error('Error extracting user info from token:', error);
                  }
                }

                // Check if user has a valid disclaimer acknowledgment
                if (!hasValidDisclaimer()) {
                  // Show disclaimer if this is the first login or disclaimer has expired
                  setIsLoginModalOpen(false);
                  setIsDisclaimerOpen(true);
                } else {
                  // Navigate directly to the chat page if already seen disclaimer
                  setIsLoginModalOpen(false);
                  setOtpError(null);
                  navigateWithReload("/");
                }
                return;
              }
            }
          } catch (error) {
            console.error('Auto-login after OTP verification failed:', error);
          }

          // If auto-login failed or was not attempted, just go to step 3
          setSignupStep(3);
          setOtpError(null);
        }
      } else {
        setOtpError(data.message || 'Invalid OTP');
      }
    } catch (error) {
      setOtpError('Failed to verify OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleCompleteSignup = async () => {
    try {
      setLoading(true);

      // Auto-login with email and password
      if (email && password) {
        const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.AUTH.TOKEN}`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email, password }),
        });

        const data = await response.json();

        if (response.ok) {
          // Store tokens and user info
          localStorage.setItem('access_token', data.access_token);
          localStorage.setItem('refresh_token', data.refresh_token);
          localStorage.setItem('authToken', data.access_token);

          if (data.user) {
            localStorage.setItem('userId', data.user.id);
            localStorage.setItem('userEmail', data.user.email);
            localStorage.setItem('userName', data.user.name || '');
          } else {
            try {
              const payload = decodeJWTPayload(data.access_token);
              if (payload && payload.user_id) {
                localStorage.setItem('userId', payload.user_id);
              }
              if (payload && payload.email) {
                localStorage.setItem('userEmail', payload.email);
              }
            } catch (error) {
              console.error('Error extracting user info from token:', error);
            }
          }

          // Check if user has a valid disclaimer acknowledgment
          if (!hasValidDisclaimer()) {
            // Show disclaimer if this is the first login or disclaimer has expired
            setIsLoginModalOpen(false);
            setIsDisclaimerOpen(true);
          } else {
            // Navigate directly to the chat page if already seen disclaimer
            setIsLoginModalOpen(false);
            navigateWithReload("/");
          }
        } else {
          setLoginError(data.message || 'Login failed after signup');
        }
      } else {
        setLoginError('Email or password missing');
      }
    } catch (error) {
      setLoginError('Failed to login after signup');
    } finally {
      setLoading(false);
    }
  };

  // Updated Token Refresh Logic - don't show login popup automatically
  useEffect(() => {
    let refreshTimeout;

    const checkAndRefreshToken = async () => {
      const token = localStorage.getItem('access_token');
      if (!token) {
        // Don't auto-show login if no token
        return;
      }

      const payload = decodeJWTPayload(token);
      if (!payload || !payload.exp) {
        // Just clear tokens but don't auto-show login
        localStorage.removeItem('access_token');
        localStorage.removeItem('refresh_token');
        localStorage.removeItem('authToken');
        localStorage.removeItem('userId');
        localStorage.removeItem('userEmail');
        localStorage.removeItem('userName');
        return;
      }

      const currentTime = Math.floor(Date.now() / 1000);
      const timeUntilExpiry = payload.exp - currentTime;

      // Refresh if token is expired or expires in <5 minutes
      if (timeUntilExpiry < 300) {
        // console.log(`Token expires in ${timeUntilExpiry} seconds, refreshing...`);
        try {
          // Don't pass setIsLoginModalOpen to avoid auto-showing login
          const newToken = await refreshAccessToken(null);
          if (newToken) {
            // console.log('Token refreshed successfully');
          } else {
            console.warn('Token refresh failed');
            // Don't auto-show login
          }
        } catch (error) {
          console.error('Token refresh error:', error);
          // Don't auto-show login
        }
      } else {
        // console.log(`Token valid for ${timeUntilExpiry} seconds`);
      }

      // Schedule next check based on expiration time
      const nextCheck = Math.max(timeUntilExpiry - 300, 60) * 1000; // At least 60s
      refreshTimeout = setTimeout(checkAndRefreshToken, nextCheck);
    };

    // Initial check
    checkAndRefreshToken();

    return () => clearTimeout(refreshTimeout);
  }, []);

  useEffect(() => {
    if (passwordResetSuccess) {
      const timer = setTimeout(() => {
        setPasswordResetSuccess(null);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [passwordResetSuccess]);

  useEffect(() => {
    if (loginError || emptyFieldsError) {
      const timer = setTimeout(() => {
        setLoginError(null);
        setEmptyFieldsError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [loginError, emptyFieldsError]);

  useEffect(() => {
    if (resendOtpError) {
      const timer = setTimeout(() => {
        setResendOtpError(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [resendOtpError]);

  useEffect(() => {
    if (resendOtpSuccess) {
      const timer = setTimeout(() => {
        setResendOtpSuccess(null);
      }, 5000);
      return () => clearTimeout(timer);
    }
  }, [resendOtpSuccess]);

  const handleGoogleLogin = async () => {
    try {
      setLoading(true);
      setLoginError(null);
      window.location.href = `${API_CONFIG.BASE_URL}${API_CONFIG.AUTH.GOOGLE}`;
    } catch (error) {
      console.error('Google login error:', error);
    }
  };

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const accessToken = params.get('access_token');
    const refreshToken = params.get('refresh_token');

    if (accessToken && refreshToken) {
        localStorage.setItem('access_token', accessToken);
        localStorage.setItem('refresh_token', refreshToken);
        localStorage.setItem('authToken', accessToken);
        localStorage.setItem('userName', params.get('name'));
        // Add this for compatibility

        // Extract and store userId from token
        try {
            const payload = decodeJWTPayload(accessToken);
            // console.log(payload);
            if (payload && payload.user_id) {
                localStorage.setItem('userId', payload.user_id);
            }
            if (payload && payload.email) {
                localStorage.setItem('userEmail', payload.email);
            }
            if (payload && payload.name) {
                localStorage.setItem('userName', payload.name);
            }
        } catch (error) {
            console.error('Error extracting user ID from token:', error);
        }

        // Check if user has a valid disclaimer acknowledgment
        
        // Close login modal
        setIsLoginModalOpen(false);
        
        if (!hasValidDisclaimer()) {
            // Show disclaimer if this is the first login or disclaimer has expired
            setIsDisclaimerOpen(true);
        } else {
            // Navigate directly to chat page with reload
            navigateWithReload("/");
        }
    }

    if (location.pathname === '/auth/error') {
        setLoginError('Google authentication failed. Please try again.');
    }
  }, [location]);

  const handleResetPassword = async () => {
    // Validate fields
    if (!otp) {
      setForgotPasswordError("Please enter the OTP sent to your email");
      return;
    }

    if (!newPassword) {
      setForgotPasswordError("Please enter a new password");
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordMatchError("Passwords do not match");
      return;
    }

    try {
      setLoading(true);
      setForgotPasswordError(null);
      setPasswordMatchError(null);

      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.AUTH.RESET_PASSWORD}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: forgotPasswordEmail,
          otp: otp,
          new_password: newPassword
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // Show success message and return to login
        setShowForgotPassword(false);
        setResetPasswordStep(1); // Reset back to initial state
        setPasswordResetSuccess(data.message || 'Password reset successful! Please login with your new password.');

        // Clear the forms
        setOtp("");
        setNewPassword("");
        setConfirmPassword("");
        setForgotPasswordEmail("");
      } else {
        setForgotPasswordError(data.message || 'Failed to reset password');
      }
    } catch (error) {
      setForgotPasswordError('Failed to process request');
    } finally {
      setLoading(false);
    }
  };

  const handleResendResetOtp = async () => {
    try {
      setLoading(true);
      setForgotPasswordError(null);

      const response = await fetch(`${API_CONFIG.BASE_URL}${API_CONFIG.AUTH.RESEND_OTP}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: forgotPasswordEmail,
          purpose: "password_reset"
        }),
      });

      const data = await response.json();

      if (response.ok) {
        setResendOtpSuccess(data.message || 'OTP sent successfully');
      } else {
        setForgotPasswordError(data.message || 'Failed to resend OTP');
      }
    } catch (error) {
      setForgotPasswordError('Failed to resend OTP');
    } finally {
      setLoading(false);
    }
  };

  // Function to handle redirect from disclaimer message popup
  const handleDisclaimerRedirect = () => {
    setShowDisclaimerMessage(false);
    navigateWithReload("/");
  };

  // The critical change is in the render method - we'll now completely remove the component from the DOM when not needed
  if (showDisclaimerMessage) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        width: '100%',
        height: '100vh',
        position: 'fixed',
        top: 0,
        left: 0,
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        zIndex: 2000
      }}>
        <div style={{
          backgroundColor: 'white',
          padding: '30px',
          borderRadius: '10px',
          boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
          maxWidth: '400px',
          width: '90%',
          textAlign: 'center',
          position: 'relative',
          zIndex: 2001
        }}>
          <h3 style={{ color: '#2241dd', marginBottom: '15px', fontWeight: 'bold', fontSize: '20px' }}>Disclaimer Required</h3>
          <p style={{ margin: '20px 0', lineHeight: '1.5' }}>Please accept the disclaimer before accessing the login page.</p>
          <button
            style={{
              marginTop: '15px',
              backgroundColor: '#2241dd',
              color: 'white',
              padding: '12px 25px',
              borderRadius: '5px',
              border: 'none',
              cursor: 'pointer',
              fontWeight: 'bold',
              boxShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
              transition: 'background-color 0.2s ease'
            }}
            onClick={handleDisclaimerRedirect}
          >
            OK
          </button>
        </div>
      </div>
    );
  }
  
  if (!isLoginModalOpen) {
    // console.log(`Login modal is hidden - returning null (no render) [${instanceId.current}]`);
    // Don't even render the backdrop
    return null; // Return nothing if the modal shouldn't be shown
  }

  // Only render the login modal if isLoginModalOpen is true
  // console.log(`Login modal is visible - rendering modal [${instanceId.current}]`);
  return (
    <div className="flex items-center justify-center min-h-screen bg-gray-100" style={{position: "fixed", top: 0, left: 0, width: "100%", height: "100%", zIndex: 2000}}>
      <div className="login-backdrop" style={{zIndex: 1999}}></div>
      <div className="popup1" style={{zIndex: 2001}}>
        <div className="popup-content">
          <div className="login-container">
            <h2>
              {isLoginView ? (
                <img src={Geohublogo} alt="GeoHub Logo" className="logos" />
              ) : signupStep === 1 ? (
                "Create your Account"
              ) : signupStep === 2 ? (
                "Check your inbox"
              ) : (
                "Tell us about you"
              )}
            </h2>
            <hr style={{width : "50%", marginLeft :"25%"}} />
            {(isLoginView || signupStep === 1) && (
              <div className="login-options">
                <button
                  onClick={handleGoogleLogin}
                  disabled={loading}
                  className="google"
                >
                  {loading ? (
                    <>
                      <span className="loading-spinner"></span>
                      Connecting...
                    </>
                  ) : (
                    "Continue with Google"
                  )}
                </button>
              </div>
            )}

            {!(!isLoginView && (signupStep === 2 || signupStep === 3)) && (
              <div className="email-option">
                {isLoginView ? (
                  <span>
                    Don't have an account? <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setIsLoginView(false);
                      }}
                    >
                      Sign Up
                    </a>
                  </span>
                ) : (
                  <span>
                    Already have an account? <a
                      href="#"
                      onClick={(e) => {
                        e.preventDefault();
                        setIsLoginView(true);
                      }}
                    >
                      Log in
                    </a>
                  </span>
                )}
                <span>------- or Continue with Email -------</span>
              </div>
            )}

            <div className="email-login">
              {isLoginView ? (
                <form
                  onSubmit={async (e) => {
                    e.preventDefault();
                    await handleLogin();
                  }}
                  autoComplete="on"
                >
                  <div className="input-group">
                    <input
                      type="email"
                      placeholder="Email"
                      required
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>
                  <div className="input-group">
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder="Password"
                      required
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <span className="password-toggle-icon" onClick={togglePasswordVisibility}>
                      <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                    </span>
                  </div>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%', marginBottom: '10px' }}>
                    <div className="remember-me">
                      <input id="remember-me" type="checkbox" />
                      <label htmlFor="remember-me">Remember me</label>
                    </div>
                    <a
                      href="#"
                      style={{ fontSize: '14px' }}
                      onClick={(e) => {
                        e.preventDefault();
                        handleForgotPasswordClick();
                      }}
                    >
                      Forgot your password
                    </a>
                  </div>
                  {emptyFieldsError && <p style={{ color: 'red', marginTop: '10px', fontSize: '14px' }}>{emptyFieldsError}</p>}
                  {loginError && <p style={{ color: 'red', marginTop: '10px', fontSize: '14px' }}>{loginError}</p>}
                  <button 
                    type="submit"
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="loading-spinner"></span>
                        Logging in...
                      </>
                    ) : (
                      "Log in"
                    )}
                  </button>
                </form>
              ) : signupStep === 1 ? (
                <>
                  <div className="input-group">
                    <input
                      type="text"
                      placeholder="Full Name"
                      required
                      onChange={(e) => setName(e.target.value)}
                    />
                  </div>

                  <div className="input-group">
                    <input
                      type="email"
                      placeholder="Email"
                      required
                      onChange={(e) => setEmail(e.target.value)}
                    />
                  </div>

                  <div className="input-group">
                    <input
                      type={showPassword ? "text" : "password"}
                      placeholder="Password"
                      required
                      onChange={(e) => setPassword(e.target.value)}
                    />
                    <span className="password-toggle-icon" onClick={togglePasswordVisibility}>
                      <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                    </span>
                  </div>

                  {loginError && <p style={{ color: 'red', marginTop: '10px', fontSize: '14px' }}>{loginError}</p>}

                  <button
                    type="submit"
                    onClick={handleSignup}
                    disabled={loading}
                  >
                    {loading ? (
                      <>
                        <span className="loading-spinner"></span>
                        Creating account...
                      </>
                    ) : (
                      "Create Account"
                    )}
                  </button>
                </>
              ) : signupStep === 2 ? (
                <>
                  <div style={{ textAlign: 'center', margin: '10px 0' }}>
                    <p>Enter the verification code we just sent to</p>
                    <p style={{ fontWeight: 'bold', margin: '10px 0' }}>{email}</p>

                    <div className="input-group" style={{ marginTop: '15px' }}>
                      <input
                        type="text"
                        placeholder="Enter verification code"
                        required
                        value={otp}
                        onChange={(e) => setOtp(e.target.value)}
                      />
                    </div>

                    {otpError && <p style={{ color: 'red', marginTop: '10px', fontSize: '14px' }}>{otpError}</p>}

                    <button
                      type="submit"
                      onClick={handleVerifyOtp}
                      disabled={loading}
                    >
                      {loading ? (
                        <>
                          <span className="loading-spinner"></span>
                          Verifying...
                        </>
                      ) : (
                        "Verify"
                      )}
                    </button>

                    <div className="resend-otp-link">
                      <a href="#" onClick={(e) => {
                        e.preventDefault();
                        handleResendOtp();
                      }}>
                        Resend verification code
                      </a>
                    </div>

                    {resendOtpError && <p style={{ color: 'red', fontSize: '14px' }}>{resendOtpError}</p>}
                    {resendOtpSuccess && <p style={{ color: 'green', fontSize: '14px' }}>{resendOtpSuccess}</p>}
                  </div>
                </>
              ) : signupStep === 3 ? (
                <>
                  <div style={{ textAlign: 'center', margin: '10px 0' }}>
                    <h3 style={{ color: '#2241dd', marginBottom: '15px' }}>Verification Successful!</h3>
                    <p>Your account has been successfully verified.</p>

                    {loginError && <p style={{ color: 'red', marginTop: '10px', fontSize: '14px' }}>{loginError}</p>}

                    <button
                      type="button"
                      onClick={handleCompleteSignup}
                      disabled={loading}
                      style={{ marginTop: '20px' }}
                    >
                      {loading ? (
                        <>
                          <span className="loading-spinner"></span>
                          Redirecting...
                        </>
                      ) : (
                        "Continue to Chat"
                      )}
                    </button>
                  </div>
                </>
              ) : (
                <></>
              )}
            </div>

            {showForgotPassword && (
              <div className="popup1">
                <div className="popup-content">
                  <div className="login-container">
                    {resetPasswordStep === 1 ? (
                      <>
                        <h2>Forgot Your Password</h2>
                        <p style={{ marginBottom: '20px', fontSize: '15px' }}>
                          Enter your email address and we'll send you a verification code to reset your password.
                        </p>

                        <div className="input-group">
                          <input
                            type="email"
                            placeholder="Enter your email"
                            value={forgotPasswordEmail}
                            onChange={(e) => setForgotPasswordEmail(e.target.value)}
                            required
                          />
                        </div>

                        {forgotPasswordError && <p style={{ color: 'red', marginTop: '10px', fontSize: '14px' }}>{forgotPasswordError}</p>}

                        <button
                          onClick={handleForgotPasswordSubmit}
                          disabled={loading}
                          style={{ marginBottom: '10px' }}
                        >
                          {loading ? (
                            <>
                              <span className="loading-spinner"></span>
                              Sending...
                            </>
                          ) : (
                            "Send Reset Code"
                          )}
                        </button>

                        <button
                          onClick={() => {
                            setShowForgotPassword(false);
                            setResetPasswordStep(1);
                            setForgotPasswordError(null);
                          }}
                          style={{
                            backgroundColor: '#f5f5f5',
                            color: '#333'
                          }}
                        >
                          Cancel
                        </button>
                      </>
                    ) : (
                      <>
                        <h2>Reset Your Password</h2>
                        <p style={{ marginBottom: '20px', fontSize: '15px' }}>
                          We've sent a verification code to <strong>{forgotPasswordEmail}</strong>
                        </p>

                        <div className="input-group">
                          <input
                            type="text"
                            placeholder="Enter verification code"
                            value={otp}
                            onChange={(e) => setOtp(e.target.value)}
                            required
                          />
                        </div>

                        <div className="input-group">
                          <input
                            type={showPassword ? "text" : "password"}
                            placeholder="New Password"
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            required
                          />
                          <span className="password-toggle-icon" onClick={togglePasswordVisibility}>
                            <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                          </span>
                        </div>

                        <div className="input-group">
                          <input
                            type={showPassword ? "text" : "password"}
                            placeholder="Confirm New Password"
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            required
                          />
                        </div>

                        {forgotPasswordError && <p style={{ color: 'red', marginTop: '10px', fontSize: '14px' }}>{forgotPasswordError}</p>}
                        {passwordMatchError && <p style={{ color: 'red', marginTop: '10px', fontSize: '14px' }}>{passwordMatchError}</p>}
                        {resendOtpSuccess && <p style={{ color: 'green', marginTop: '10px', fontSize: '14px' }}>{resendOtpSuccess}</p>}

                        <button
                          onClick={handleResetPassword}
                          disabled={loading}
                          style={{ marginBottom: '10px' }}
                        >
                          {loading ? (
                            <>
                              <span className="loading-spinner"></span>
                              Resetting...
                            </>
                          ) : (
                            "Reset Password"
                          )}
                        </button>

                        <div className="resend-otp-link">
                          <a href="#" onClick={(e) => {
                            e.preventDefault();
                            handleResendResetOtp();
                          }}>
                            Resend verification code
                          </a>
                        </div>

                        <button
                          onClick={() => {
                            setShowForgotPassword(false);
                            setResetPasswordStep(1);
                            setForgotPasswordError(null);
                          }}
                          style={{
                            backgroundColor: '#f5f5f5',
                            color: '#333'
                          }}
                        >
                          Cancel
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            )}

            {passwordResetSuccess && (
              <div className="fixed top-0 left-0 w-full h-full flex items-center justify-center bg-black bg-opacity-50 z-50">
                <div className="popups1">
                  <div className="popup-contents">
                    <p style={{ color: 'green', fontSize: '16px', fontWeight: 'bold' }}>{passwordResetSuccess}</p>
                    <button
                      style={{
                        marginTop: '15px',
                        backgroundColor: '#2241dd',
                        color: 'white',
                        padding: '8px 20px',
                        borderRadius: '5px',
                        border: 'none',
                        cursor: 'pointer'
                      }}
                      onClick={() => setPasswordResetSuccess(null)}
                    >
                      OK
                    </button>
                  </div>
                </div>
              </div>
            )}

            <div className="footer">
              <a href="https://www.encardio.com/terms-of-use" target="_blank" rel="noopener noreferrer">Terms of Use</a> | <a href="https://www.encardio.com/privacy-policies" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
            </div>
          </div>
        </div>
      </div>

      {isDisclaimerOpen && (
        <Disclaimer 
          onUnderstand={handleUnderstandDisclaimer}
          onClose={handleCloseDisclaimer}
        />
      )}

      {loading && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="animate-spin rounded-full h-16 w-16 border-t-4 border-b-4 border-blue-500"></div>
        </div>
      )}
    </div>
  );
};

export default Login;
export { refreshAccessToken, createAuthenticatedRequest, isTokenExpired, validateToken, clearAuthTokens };







// <button className="facebook" onClick={() => handleOAuthLogin("facebook")} disabled={loading}>
//               {loading ? "Processing..." : "Continue with Facebook"}
//             </button>
//             <button className="apple" onClick={() => handleOAuthLogin("apple")} disabled={loading}>
//               {loading ? "Processing..." : "Continue with Apple"}
//             </button>